import { Component, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal, NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ModalDismissStatus } from '../../../distribution/models/modal-dismiss-status';
import { ToastService, ToastType } from '../../../shared/toast/toast.service';
import { Router } from '@angular/router';
import { PresentationMonitorItem, PresentationMonitorItemStatus } from '../../models/monitor-status';
import { EmbeddedItemsService } from '../../services/embedded-items.service';
import { DateTimeService } from '../../../shared/datetime/datetime.service';
import { MonitorAbsenceDialogComponent, MonitorAbsenceDialogResult } from '../monitor-absence-dialog/monitor-absence-dialog.component';
import { GenericModalService, ModalSettingsTemplate } from 'src/app/shared/generic-modal/generic-modal.service';
import { AbsenceItemTemplate, Weekdays } from '../../../shared/scheduling/models/';
import { MonitorItemContainer } from '../../models/monitor-item';
import { MonitorService } from '../../services/monitor.service';
import { LoadingPanelService } from '../../../shared/loading-panel/loading-panel.service';

import { DxDataGridModule } from 'devextreme-angular';
import { DxoSelectionModule, DxiColumnModule } from 'devextreme-angular/ui/nested';
import { DxTemplateModule } from 'devextreme-angular/core';
import { StatusTrafficLightComponent } from '../../../shared/status-traffic-light/status-traffic-light.component';
import { PermissionRequiredDirective } from 'src/app/shared/authentication/permission-required.directive';

@Component({
    templateUrl: './monitor-item-dialog.component.html',
    styleUrls: ['./monitor-item-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [DxDataGridModule, DxoSelectionModule, DxiColumnModule, DxTemplateModule, NgbTooltip, StatusTrafficLightComponent, PermissionRequiredDirective]
})
export class MonitorItemDialogComponent {

    public selectedMonitorItem: PresentationMonitorItem;
    public selectedMonitorItemStatuses: PresentationMonitorItemStatus[] = [];

    constructor(
        public modal: NgbActiveModal,
        private router: Router,
        private toastService: ToastService,
        private embeddedItemsService: EmbeddedItemsService,
        private modalService: GenericModalService,
        private monitorService: MonitorService,
        private loadingPanelService: LoadingPanelService,
        public dateTimeService: DateTimeService){
        }

    cancel(): void {
        this.modal.dismiss(ModalDismissStatus.Cancel);
    }

    canGoToMonitorEdit() {
        if(this.selectedMonitorItem == null) {
            return false;
        } else if (this.selectedMonitorItem.EmbeddedTypeArea != 'None') {
            return this.embeddedItemsService.canNavigateToEmbeddedItem(this.selectedMonitorItem.EmbeddedTypeArea);
        } else if (this.selectedMonitorItem.ArtifactId != null) {
            return true;
        } else {
            return false;
        }
    }

    async goToMonitorEdit() {
        try {
            if(this.selectedMonitorItem == null) {
                this.toastService.showToast('Failed navigating to monitor item...', ToastType.error);
                return;
            } else if (this.selectedMonitorItem.EmbeddedTypeArea != 'None' && this.selectedMonitorItem.EmbeddedTypeId != null) {
                await this.embeddedItemsService.navigateToEmbeddedItem(this.selectedMonitorItem.EmbeddedTypeArea, this.selectedMonitorItem.EmbeddedTypeId)
            } else if (this.selectedMonitorItem.ArtifactId != null) {
                const id = this.selectedMonitorItem.Id;
                await this.router.navigate(['monitor/items/item', { id }]);
            } else {
                this.toastService.showToast('Failed navigating to monitor item...', ToastType.error);
                return;
            }
        }
        finally {
            this.modal.close();
        }
    }

    async pauseMonitor(): Promise<void> {
        try {
            const modalSettings = new ModalSettingsTemplate(null, 'static');
            const result: MonitorAbsenceDialogResult = await this.modalService.openComponent(MonitorAbsenceDialogComponent, null, modalSettings);
            if (result !== undefined) {
                let dateTimeNow = new Date();
                let datetimeTo = new Date(result.toDate);
                const fixedTemplate: AbsenceItemTemplate = {
                    Id: null,
                    Name: 'Monitor Pause: ' + this.selectedMonitorItem.Name,
                    From: dateTimeNow,
                    To: datetimeTo,
                    Note: result.description,
                    IsGlobal: false,
                    Weekdays: Weekdays.Monday | Weekdays.Tuesday | Weekdays.Wednesday | Weekdays.Thursday | Weekdays.Friday | Weekdays.Saturday | Weekdays.Sunday
                };
                let monitorItem = await this.monitorService.getMonitorItem(this.selectedMonitorItem.Id);
                monitorItem.absenceItemTemplate = fixedTemplate;
                const item: MonitorItemContainer = {
                    monitorItem: monitorItem,
                    configValues: null
                };
                this.loadingPanelService.showLoadingPanelDirect('Pausing monitor...');
                await this.monitorService.saveMonitorItem(item);
                this.loadingPanelService.hideLoadingPanel();
                this.modal.close(true);
            }
        }
        catch (err) {
            this.modalService.messageDirect('Exception', 'Error occured while pausing monitor').catch(() => { });
        }
    }


    async resumeMonitor(): Promise<void> {
        try {

            let monitorItem = await this.monitorService.getMonitorItem(this.selectedMonitorItem.Id);
            monitorItem.absenceItemTemplate = null;
            const item: MonitorItemContainer = {
                monitorItem: monitorItem,
                configValues: null
            };
            this.loadingPanelService.showLoadingPanelDirect('Resuming monitor...');
            await this.monitorService.saveMonitorItem(item);
            this.loadingPanelService.hideLoadingPanel();
            this.modal.close(true);
        }
        catch (err) {
            this.modalService.messageDirect('Exception', 'Error occured while pausing monitor').catch(() => { });
        }
    }

    getAbsenceExpireDate(): string {
        let returnDate = '';
        if (this.selectedMonitorItem.AbsenceItemTemplate?.To) {
            let toDate = this.selectedMonitorItem.AbsenceItemTemplate.To.toString()
            returnDate = `${toDate.substring(0, 10)} : ${toDate.substring(11, 16)}`;
        }
        return returnDate;
    }
}
